import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  KeyIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchLocations } from '../store/locationsSlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import { fetchInventory } from '../store/keyInventorySlice';
import { fetchAssignments, createAssignmentAsync, returnKeyAsync } from '../store/assignmentsSlice';
import { fetchPeople } from '../store/peopleSlice';
import { Visitor, KeyAssignment, KeyInventoryItem } from '../types';
import { visitorsApi } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';

interface VisitorFormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  purpose: string;
  hostPersonId: string;
  badgeNumber: string;
  expectedCheckOutTime: string;
  notes: string;
}

interface AssignKeyFormData {
  keyTypeId: string;
  quantity: number;
  notes: string;
}

export default function Visitors() {
  const dispatch = useAppDispatch();
  const locations = useAppSelector((state) => state.locations.locations);
  const currentLocationId = useAppSelector((state) => state.locations.currentLocationId);
  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);
  const inventory = useAppSelector((state) => state.keyInventory.inventory);
  const assignments = useAppSelector((state) => state.assignments.assignments);
  const people = useAppSelector((state) => state.people.people);
  const currentUser = useAppSelector((state) => state.users.currentUser);

  // Get the current location object
  const currentLocation = useMemo(() => {
    return locations.find(loc => loc.id === currentLocationId);
  }, [locations, currentLocationId]);

  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'checked-in' | 'checked-out'>('checked-in');
  const [showAllLocations, setShowAllLocations] = useState(false);

  // Modal states
  const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(false);
  const [isAssignKeyModalOpen, setIsAssignKeyModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState<Visitor | null>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [checkoutNotes, setCheckoutNotes] = useState('');

  const visitorForm = useForm<VisitorFormData>();
  const assignKeyForm = useForm<AssignKeyFormData>();

  useEffect(() => {
    loadVisitors();
    dispatch(fetchLocations());
    dispatch(fetchKeyTypes());
    dispatch(fetchInventory());
    dispatch(fetchAssignments());
    dispatch(fetchPeople());
  }, [dispatch]);

  const loadVisitors = async () => {
    try {
      const data = await visitorsApi.getAll();
      setVisitors(data);
    } catch (error) {
      console.error('Failed to load visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get assignments for a visitor (using visitor ID as personId with 'visitor-' prefix)
  const getVisitorAssignments = (visitorId: string) => {
    return assignments.filter(a => a.personId === `visitor-${visitorId}` && a.status === 'active');
  };

  // Check if a visitor is overdue (checked in with expected checkout time in the past)
  const isVisitorOverdue = (visitor: Visitor): boolean => {
    if (visitor.status !== 'checked-in') return false;
    if (!visitor.expectedCheckOutTime) return false;
    return new Date(visitor.expectedCheckOutTime) < new Date();
  };

  // Calculate hours overdue for display
  const getHoursOverdue = (visitor: Visitor): number => {
    if (!visitor.expectedCheckOutTime) return 0;
    const overdueMs = Date.now() - new Date(visitor.expectedCheckOutTime).getTime();
    return Math.round(overdueMs / (1000 * 60 * 60));
  };

  const filteredVisitors = useMemo(() => {
    const filtered = visitors.filter((visitor) => {
      const matchesSearch = 
        visitor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.badgeNumber?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || visitor.status === statusFilter;
      const matchesLocation = showAllLocations || visitor.locationId === currentLocationId;
      return matchesSearch && matchesStatus && matchesLocation;
    });

    // Sort overdue visitors to the top
    return filtered.sort((a, b) => {
      const aOverdue = isVisitorOverdue(a);
      const bOverdue = isVisitorOverdue(b);
      
      // Overdue visitors first
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      
      // Within overdue group, sort by how long overdue (most overdue first)
      if (aOverdue && bOverdue) {
        const aTime = new Date(a.expectedCheckOutTime!).getTime();
        const bTime = new Date(b.expectedCheckOutTime!).getTime();
        return aTime - bTime; // Earlier expected time = more overdue
      }
      
      // For non-overdue, sort by check-in time (most recent first)
      return new Date(b.checkInTime).getTime() - new Date(a.checkInTime).getTime();
    });
  }, [visitors, searchTerm, statusFilter, showAllLocations, currentLocationId]);

  const handleOpenVisitorModal = (visitor?: Visitor) => {
    if (visitor) {
      setEditingVisitor(visitor);
      visitorForm.setValue('name', visitor.name);
      visitorForm.setValue('company', visitor.company || '');
      visitorForm.setValue('email', visitor.email || '');
      visitorForm.setValue('phone', visitor.phone || '');
      visitorForm.setValue('purpose', visitor.purpose || '');
      visitorForm.setValue('hostPersonId', visitor.hostPersonId || '');
      visitorForm.setValue('badgeNumber', visitor.badgeNumber || '');
      visitorForm.setValue('expectedCheckOutTime', visitor.expectedCheckOutTime?.slice(0, 16) || '');
      visitorForm.setValue('notes', visitor.notes || '');
    } else {
      setEditingVisitor(null);
      visitorForm.reset();
    }
    setIsVisitorModalOpen(true);
  };

  const handleCloseVisitorModal = () => {
    setIsVisitorModalOpen(false);
    setEditingVisitor(null);
    visitorForm.reset();
  };

  const handleOpenAssignKeyModal = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    assignKeyForm.reset({ 
      quantity: 1,
    });
    setIsAssignKeyModalOpen(true);
  };

  const handleCloseAssignKeyModal = () => {
    setIsAssignKeyModalOpen(false);
    setSelectedVisitor(null);
    assignKeyForm.reset();
  };

  const handleOpenCheckoutModal = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setCheckoutNotes('');
    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false);
    setSelectedVisitor(null);
    setCheckoutNotes('');
  };

  const onSubmitVisitor = async (data: VisitorFormData) => {
    if (!currentUser || !currentLocationId) return;
    setIsSaving(true);

    try {
      if (editingVisitor) {
        const updated: Visitor = {
          ...editingVisitor,
          name: data.name,
          company: data.company || undefined,
          email: data.email || undefined,
          phone: data.phone || undefined,
          purpose: data.purpose || undefined,
          hostPersonId: data.hostPersonId || undefined,
          badgeNumber: data.badgeNumber || undefined,
          expectedCheckOutTime: data.expectedCheckOutTime ? new Date(data.expectedCheckOutTime).toISOString() : undefined,
          notes: data.notes || undefined,
        };
        await visitorsApi.update(updated);
      } else {
        const newVisitor: Visitor = {
          id: `visitor-${Date.now()}`,
          name: data.name,
          company: data.company || undefined,
          email: data.email || undefined,
          phone: data.phone || undefined,
          purpose: data.purpose || undefined,
          hostPersonId: data.hostPersonId || undefined,
          badgeNumber: data.badgeNumber || undefined,
          locationId: currentLocationId,
          expectedCheckOutTime: data.expectedCheckOutTime ? new Date(data.expectedCheckOutTime).toISOString() : undefined,
          notes: data.notes || undefined,
          checkInTime: new Date().toISOString(),
          status: 'checked-in',
          createdBy: currentUser.id,
        };
        await visitorsApi.create(newVisitor);
      }
      await loadVisitors();
      handleCloseVisitorModal();
    } catch (error) {
      console.error('Failed to save visitor:', error);
      alert('Failed to save visitor. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmitAssignKey = async (data: AssignKeyFormData) => {
    if (!selectedVisitor || !currentUser || !currentLocationId) return;
    setIsSaving(true);

    try {
      // Find inventory item at current location
      let inventoryItem = inventory.find(
        (item: KeyInventoryItem) => item.keyTypeId === data.keyTypeId && item.locationId === currentLocationId
      );

      if (!inventoryItem) {
        alert('No inventory exists for this key type at the current location. Please add inventory first.');
        setIsSaving(false);
        return;
      }

      const newAssignment: KeyAssignment = {
        id: `assignment-${Date.now()}`,
        keyInventoryItemId: inventoryItem.id,
        personId: `visitor-${selectedVisitor.id}`, // Prefix with 'visitor-' to distinguish from regular people
        quantity: data.quantity,
        assignedAt: new Date().toISOString(),
        assignedBy: currentUser.name,
        notes: data.notes ? `[Visitor: ${selectedVisitor.name}] ${data.notes}` : `[Visitor: ${selectedVisitor.name}]`,
        status: 'active',
      };

      await dispatch(createAssignmentAsync(newAssignment)).unwrap();
      handleCloseAssignKeyModal();
    } catch (error) {
      console.error('Failed to assign key:', error);
      alert('Failed to assign key. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedVisitor || !currentUser) return;
    setIsSaving(true);

    try {
      // First, return all keys assigned to this visitor
      const visitorAssignments = getVisitorAssignments(selectedVisitor.id);
      for (const assignment of visitorAssignments) {
        await dispatch(returnKeyAsync({
          id: assignment.id,
          returnedBy: currentUser.name,
          notes: `Auto-returned on visitor checkout`,
        })).unwrap();
      }

      // Then check out the visitor
      await visitorsApi.checkOut(selectedVisitor.id, checkoutNotes);
      await loadVisitors();
      handleCloseCheckoutModal();
    } catch (error) {
      console.error('Failed to check out visitor:', error);
      alert('Failed to check out visitor. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReturnKey = async (assignment: KeyAssignment) => {
    if (!currentUser) return;
    if (!confirm('Return this key?')) return;

    try {
      await dispatch(returnKeyAsync({
        id: assignment.id,
        returnedBy: currentUser.name,
      })).unwrap();
    } catch (error) {
      console.error('Failed to return key:', error);
      alert('Failed to return key.');
    }
  };

  const handleDeleteVisitor = async (visitor: Visitor) => {
    if (!confirm(`Delete visitor "${visitor.name}"? This cannot be undone.`)) return;

    try {
      await visitorsApi.delete(visitor.id);
      await loadVisitors();
    } catch (error) {
      console.error('Failed to delete visitor:', error);
      alert('Failed to delete visitor.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-secondary-600 dark:text-secondary-400">Loading visitors...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Visitors</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Manage visitor check-ins and temporary key assignments
          </p>
        </div>
        <Button onClick={() => handleOpenVisitorModal()}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Check In Visitor
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Search by name, company, or badge..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'checked-in' | 'checked-out')}
            className="px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
          >
            <option value="checked-in">Checked In</option>
            <option value="checked-out">Checked Out</option>
            <option value="all">All Status</option>
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showAllLocations}
              onChange={(e) => setShowAllLocations(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-nowrap">
              Show all locations
            </span>
          </label>
        </div>
      </Card>

      {/* Visitors List */}
      <div className="grid gap-4">
        {filteredVisitors.length === 0 ? (
          <Card>
            <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
              {searchTerm || statusFilter !== 'checked-in' || showAllLocations
                ? 'No visitors match your search criteria'
                : 'No visitors checked in. Click "Check In Visitor" to add one.'}
            </div>
          </Card>
        ) : (
          filteredVisitors.map((visitor) => {
            const location = locations.find(l => l.id === visitor.locationId);
            const host = visitor.hostPersonId ? people.find(p => p.id === visitor.hostPersonId) : null;
            const visitorAssignments = getVisitorAssignments(visitor.id);
            const overdue = isVisitorOverdue(visitor);
            const hoursOverdue = overdue ? getHoursOverdue(visitor) : 0;

            return (
              <Card 
                key={visitor.id}
                className={overdue ? 'border-2 border-red-500 dark:border-red-400 bg-red-50/50 dark:bg-red-900/10' : ''}
              >
                {/* Overdue Banner */}
                {overdue && (
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-red-200 dark:border-red-800">
                    <ClockIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                      OVERDUE - {hoursOverdue > 0 ? `${hoursOverdue} hour${hoursOverdue !== 1 ? 's' : ''} past expected checkout` : 'Past expected checkout time'}
                    </span>
                  </div>
                )}
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                        {visitor.name}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        visitor.status === 'checked-in'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400'
                      }`}>
                        {visitor.status === 'checked-in' ? 'Checked In' : 'Checked Out'}
                      </span>
                      {overdue && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                          Overdue
                        </span>
                      )}
                      {visitor.badgeNumber && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                          Badge: {visitor.badgeNumber}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-secondary-600 dark:text-secondary-400">
                      {visitor.company && <p>Company: {visitor.company}</p>}
                      {location && <p>Location: {location.name}</p>}
                      {visitor.purpose && <p>Purpose: {visitor.purpose}</p>}
                      {host && <p>Host: {host.name}</p>}
                      <p>Checked in: {new Date(visitor.checkInTime).toLocaleString()}</p>
                      {visitor.expectedCheckOutTime && (
                        <p className={overdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                          Expected out: {new Date(visitor.expectedCheckOutTime).toLocaleString()}
                        </p>
                      )}
                      {visitor.actualCheckOutTime && (
                        <p>Checked out: {new Date(visitor.actualCheckOutTime).toLocaleString()}</p>
                      )}
                    </div>

                    {/* Assigned Keys */}
                    {visitorAssignments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                        <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          Assigned Keys
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {visitorAssignments.map((assignment) => {
                            const invItem = inventory.find(i => i.id === assignment.keyInventoryItemId);
                            const keyType = keyTypes.find(kt => kt.id === invItem?.keyTypeId);
                            return (
                              <span
                                key={assignment.id}
                                className="inline-flex items-center gap-2 px-3 py-1 bg-warning/10 dark:bg-warning/20 rounded-full text-sm text-warning-700 dark:text-warning-300"
                              >
                                <KeyIcon className="w-4 h-4" />
                                {keyType?.name || 'Unknown'} x{assignment.quantity}
                                {visitor.status === 'checked-in' && (
                                  <button
                                    onClick={() => handleReturnKey(assignment)}
                                    className="ml-1 p-0.5 hover:bg-warning/20 rounded"
                                    title="Return key"
                                  >
                                    <ArrowRightOnRectangleIcon className="w-3 h-3" />
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {visitor.status === 'checked-in' && (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenAssignKeyModal(visitor)}
                        >
                          <KeyIcon className="w-4 h-4 mr-1" />
                          Assign Key
                        </Button>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleOpenCheckoutModal(visitor)}
                        >
                          <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1" />
                          Check Out
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleOpenVisitorModal(visitor)}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteVisitor(visitor)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Visitor Modal */}
      <Modal
        isOpen={isVisitorModalOpen}
        onClose={handleCloseVisitorModal}
        title={editingVisitor ? 'Edit Visitor' : 'Check In Visitor'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseVisitorModal}>
              Cancel
            </Button>
            <Button onClick={visitorForm.handleSubmit(onSubmitVisitor)} disabled={isSaving || !currentLocationId}>
              {isSaving ? 'Saving...' : editingVisitor ? 'Save Changes' : 'Check In'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          {/* Show current location (read-only) */}
          <div className="p-3 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
            <div className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">
              Location
            </div>
            <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
              {editingVisitor 
                ? locations.find(l => l.id === editingVisitor.locationId)?.name || 'Unknown'
                : currentLocation?.name || 'No location selected'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Name *
            </label>
            <input
              {...visitorForm.register('name', { required: true })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Company
              </label>
              <input
                {...visitorForm.register('company')}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Badge Number
              </label>
              <input
                {...visitorForm.register('badgeNumber')}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Email
              </label>
              <input
                type="email"
                {...visitorForm.register('email')}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Phone
              </label>
              <input
                type="tel"
                {...visitorForm.register('phone')}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Host (Employee)
            </label>
            <select
              {...visitorForm.register('hostPersonId')}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            >
              <option value="">Select a host...</option>
              {people.filter(p => p.status === 'active').map((person) => (
                <option key={person.id} value={person.id}>{person.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Purpose of Visit
            </label>
            <input
              {...visitorForm.register('purpose')}
              placeholder="e.g., Meeting, Delivery, Maintenance"
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Expected Check Out
            </label>
            <input
              type="datetime-local"
              {...visitorForm.register('expectedCheckOutTime')}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Notes
            </label>
            <textarea
              {...visitorForm.register('notes')}
              rows={2}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            />
          </div>
        </form>
      </Modal>

      {/* Assign Key Modal */}
      <Modal
        isOpen={isAssignKeyModalOpen}
        onClose={handleCloseAssignKeyModal}
        title={`Assign Key to ${selectedVisitor?.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseAssignKeyModal}>
              Cancel
            </Button>
            <Button onClick={assignKeyForm.handleSubmit(onSubmitAssignKey)} disabled={isSaving || !currentLocationId}>
              {isSaving ? 'Assigning...' : 'Assign Key'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          {/* Show current location (read-only) */}
          <div className="p-3 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
            <div className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide mb-1">
              Location
            </div>
            <div className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
              {currentLocation?.name || 'No location selected'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Key Type *
            </label>
            <select
              {...assignKeyForm.register('keyTypeId', { required: true })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            >
              <option value="">Select key type...</option>
              {keyTypes.map((kt) => (
                <option key={kt.id} value={kt.id}>{kt.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              {...assignKeyForm.register('quantity', { valueAsNumber: true, min: 1 })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Notes
            </label>
            <textarea
              {...assignKeyForm.register('notes')}
              rows={2}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
            />
          </div>
        </form>
      </Modal>

      {/* Checkout Modal */}
      <Modal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        title={`Check Out ${selectedVisitor?.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseCheckoutModal}>
              Cancel
            </Button>
            <Button onClick={handleCheckout} disabled={isSaving}>
              {isSaving ? 'Processing...' : 'Check Out'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {selectedVisitor && getVisitorAssignments(selectedVisitor.id).length > 0 && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                This visitor has {getVisitorAssignments(selectedVisitor.id).length} assigned key(s) that will be automatically returned.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Checkout Notes
            </label>
            <textarea
              value={checkoutNotes}
              onChange={(e) => setCheckoutNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes about the checkout..."
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
